import * as React from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import {
  Logout,
  Menu as MenuIcon,
} from "@mui/icons-material";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@emotion/react";
import SideList from "./sideList";
import CenterSelector from "./CenterSelector";
import { useLayout } from "../../../middlewares/LayoutContext";
import drawerDim from "../../../theme/drawerDimensions";
import AlertDialog from "./logout";
import { LogoAndText } from "../../../theme/JaziTheme";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { open } from "../../../Redux/collapseSlice";
import { useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { changeLocationRoute, meRoute } from "../../../routes/apiRoutes";
import axiosInstance from "../../../axios/axiosInstance";
import { toast } from "react-toastify";
import { login, setCurrentLocation, switchLocation } from "../../../Redux/authenticationSlice";
import { convertUrlToBase64 } from "../../../utils/avatarUtils";
import { apply } from "../../../Redux/themeSlice";
function SideMenu() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { handleDrawerTransitionEnd, handleDrawerClose, mobileOpen } =
    useLayout();

  const handleOpenLogout = () => {
    setOpenLogout(true);
  };
  const { t } = useTranslation();

  const theme = useTheme();
  const isRtl = theme.direction === "rtl";
  const [openLogout, setOpenLogout] = React.useState(false);

  // Media queries for responsive behavior
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // Get sidebar collapse state from Redux
  const sidebarCollapsed = useSelector((state) => state.collapseValues.sidebarCollapsed);
  const currentUser = useSelector((state) => state.authenticationValues.currentUser);
  const colors = useSelector((state) => state.themeValues.colors);
  const currentLocation = currentUser.currentLocation ? {
    id: currentUser.currentLocation.id,
    code: currentUser.currentLocation.code || currentUser.currentLocation.location_name.substring(0, 2).toUpperCase(),
    name: currentUser.currentLocation.location_name,
    color: currentUser.currentLocation.colorPalette || '#BB2436', // Default green if no color
  } : null;
  const roles = React.useMemo(
    () => currentUser.roles?.map((roleItem) => roleItem?.name) || [],
    [currentUser]
  );
  const isSuperAdmin = React.useMemo(
    () => roles.includes("Super Admin"),
    [roles, currentUser]
  );

    // Center selection state
  const [selectedCenter, setSelectedCenter] = React.useState(currentLocation);
  // Handle center change
  const handleCenterChange = async (center) => {
    try{
      const authToken = localStorage.getItem("token");
      // During logout/session teardown, skip location switch side effects.
      if (!authToken) return;
      const switchResponse = await axiosInstance.post(changeLocationRoute, {  
          locationId: !center.isAllCenters ? center.id : null,
      }, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
    if (switchResponse.status === 200 || switchResponse.status === 201) {
      const response= await axiosInstance.get(meRoute, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const {data: meData } = response;
      if (response.status === 200 || response.status === 201) {
        // Switching centers should feel instant. Avoid re-downloading/converting the avatar
        // unless the avatar URL actually changed (base64 conversion is expensive).
        const nextAvatarUrl = meData.data.user.avatarUrl || "";
        const cachedAvatarUrl = localStorage.getItem("avatarUrl") || "";
        let avatarBase64 = localStorage.getItem("avatar") || null;

        if (nextAvatarUrl && (!avatarBase64 || cachedAvatarUrl !== nextAvatarUrl)) {
          avatarBase64 = await convertUrlToBase64(nextAvatarUrl);
          localStorage.setItem("avatarUrl", nextAvatarUrl);
          if (avatarBase64) localStorage.setItem("avatar", avatarBase64);
        }
        dispatch(
          login({
            user: meData.data.user,
            token: localStorage.getItem("token"),
            avatar: avatarBase64,
          })
        );
        dispatch(apply({ colors: colors, mainColor: meData?.data?.user?.currentLocation?.colorPalette || "#BB2436", mode: "light" }));
        toast.success("Location Changed Successfully");
        setSelectedCenter(center);
        dispatch(switchLocation(true));
        // navigate(0);
      }
    }
    // setSelectedCenter(center);
    // Here you can add logic to update the global state or make API calls
    // For example, update Redux store, change user context, etc.
  }catch(error){
    const authToken = localStorage.getItem("token");
    const statusCode = error?.response?.status;
    const errorMessage = error?.response?.data?.message || "";
    const isSessionTeardown =
      !authToken ||
      statusCode === 401 ||
      /session is expired/i.test(errorMessage);

    if (!isSessionTeardown) {
      toast.error(error?.response?.data?.message || "Eror in Switching Location");
    }
  }
  };

  // Set default collapsed state for tablet only
  React.useEffect(() => {
    if (isTablet && sidebarCollapsed === false) {
      dispatch(open({ sidebarCollapsed: true }));
    }
  }, [isTablet, sidebarCollapsed, dispatch]);

  // Toggle sidebar collapse - only for desktop
  const handleSidebarToggle = () => {
    if (isDesktop) {
      dispatch(open({ sidebarCollapsed: !sidebarCollapsed }));
    }
  };

  const makeDrawer = (tourEnabled = false) => (
    <div
      style={{
        display: "flex",
        background: theme.palette.whiteblack.main,
        flexDirection: "column",
        height: "100vh",
        borderRight: `1px solid ${theme.palette.componentBackgrounds.border}`,
        paddingInline: theme.spacing(2),
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          minHeight: drawerDim.height,
          py: 1,
          // mx: 0.5,
          position: "sticky",
          top: 0,
          zIndex: 1,
          background: theme.palette.whiteblack.main,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: isRtl ? "flex-end" : "flex-start",
            alignItems: "center",
            zoom: 0.8,
          }}
        >
          {/* Hamburger toggle button - show on desktop only */}
          {isTablet && (
            // <IconButton
            //   onClick={handleSidebarToggle}
            //   sx={{
            //     mr: 1,
            //     color: theme.palette.text.primary,
            //     '&:hover': {
            //       backgroundColor: theme.palette.action.hover,
            //     },
            //   }}
            // >
            //   <MenuIcon />
            // </IconButton>
            <img src="/fixafib-favicon.webp" alt="logo" style={{ width: 40, }} />
          )}
          {/* Hamburger toggle button - show on desktop only */}
          {isDesktop && (
            <IconButton
              onClick={handleSidebarToggle}
              sx={{
                mr: 1,
                color: theme.palette.text.primary,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          {/* Logo - hide when collapsed */}
          {!sidebarCollapsed && (
            <LogoAndText iFontSize={"1rem"} iFontWeight={700} />
          )}
        </Box>
        <Divider sx={{ my: 0, width: 0 }} />
      </Box>
         
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
          {/* {!isSuperAdmin && ( */}
          <CenterSelector 
            collapsed={sidebarCollapsed}
            selectedCenter={selectedCenter}
            onCenterChange={handleCenterChange}
          />
          {/* )} */}
          <SideList collapsed={sidebarCollapsed} tourEnabled={tourEnabled} />
          <Divider sx={{ my: 1 }} />
        </Box>
      
      <List>
        <ListItem key={"logout"} disablePadding>
          <ListItemButton
            sx={{
              display: "flex",
              flexDirection: isRtl ? "row-reverse" : "row",
              justifyContent: sidebarCollapsed ? "center" : "flex-start",
              minHeight: 48,
            }}
            onClick={handleOpenLogout}
          >
            <ListItemIcon
              sx={{
                width: 40,
                height: 25,
                minWidth: 40,
                display: "flex",
                flexDirection: isRtl ? "row-reverse" : "row",
                justifyContent: "center",
              }}
            >
              <Logout color="text" />
            </ListItemIcon>
            {!sidebarCollapsed && (
              <ListItemText
                primaryTypographyProps={{ fontWeight: 600, fontSize: "0.875rem" }}
                primary={t("logout")}
              />
            )}
          </ListItemButton>
        </ListItem>
      </List>
    </div>
  );

  return (
    <Box
      component="nav"
      sx={{ 
        width: { 
          xs: 0, // Mobile: no width initially
          sm: drawerDim.collapsedWidth, // Tablet: always collapsed
          md: sidebarCollapsed ? drawerDim.collapsedWidth : drawerDim.width 
        }, 
        flexShrink: { xs: 0, sm: 0, md: 0 },
        transition: 'width 0.3s ease-in-out',
      }}
      aria-label="mailbox folders"
    >
      {/* Mobile drawer - initially hidden, shows when mobileOpen is true */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onTransitionEnd={handleDrawerTransitionEnd}
        onClose={handleDrawerClose}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", sm: "none" }, // Only show on mobile
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: sidebarCollapsed ? drawerDim.collapsedWidth : drawerDim.width, // Full width for mobile
            border: "none",
          },
        }}
      >
        {makeDrawer(false)}
      </Drawer>
      
      {/* Tablet drawer - always collapsed */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", sm: "block", md: "none" }, // Only show on tablet
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerDim.collapsedWidth,
            border: "none",
            overflowX: 'hidden',
            overflowY: 'auto',
          },
        }}
        open
      >
        {makeDrawer(false)}
      </Drawer>
      
      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", sm: "none", md: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: sidebarCollapsed ? drawerDim.collapsedWidth : drawerDim.width,
            border: "none",
            transition: 'width 0.3s ease-in-out',
            overflowX: 'hidden',
            overflowY: 'auto',
          },
        }}
        open
      >
        {makeDrawer(true)}
      </Drawer>
      
      <AlertDialog
        open={openLogout}
        setOpenLogout={setOpenLogout}
      ></AlertDialog>
    </Box>
  );
}

export default SideMenu;